import { AssistantChatMessageData, UserChatMessageData } from '@indigohive/cogfy-types'
import { GetChatMessagesPageResultData } from '@indigohive/cogfy-types/endpoints/getChatMessagesPage'
import clsx from 'clsx'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getHashOfString, normalizeHash } from '../../../../helpers'
import { WhatsAppContent } from '../WhatsAppContent'
import { WhatsAppFooter } from '../WhatsAppFooter'
import { WhatsAppSubtitle } from '../WhatsAppSubtitle'
import { WhatsAppTitle } from '../WhatsAppTitle'
import { ChatSourcesBubbleContent } from '../ChatSourcesBubbleContent'

export type WhatsAppBubbleProps = {
  senderName: string
  displayPhoneNumber?: string
  message: GetChatMessagesPageResultData
  avatarImageSrc?: string
  hideAvatar?: boolean
  fromAuthenticatedUser: boolean
  onContextMenu?: (event: React.MouseEvent) => void
}

export function WhatsAppBubble (props: WhatsAppBubbleProps) {
  const { senderName, displayPhoneNumber, message, avatarImageSrc, hideAvatar, fromAuthenticatedUser } = props
  const { t } = useTranslation()

  const displayColor = displayPhoneNumber
    ? getDisplayColor(displayPhoneNumber)
    : undefined

  const errorMessage = (message.data as AssistantChatMessageData | UserChatMessageData)?.whatsapp?.error
    ?.map(error => error.message)
    .join('\n') ?? 'Message not delivered'

  return (
    <div className={clsx(message.status === 'failed' && 'flex')}>
      <div
        className={clsx(
          'chat',
          !fromAuthenticatedUser && 'chat-start',
          fromAuthenticatedUser && 'chat-end'
        )}
      >
        {/* Avatar */}
        <div
          className={clsx('chat-image avatar', hideAvatar && 'invisible', !avatarImageSrc && 'placeholder')}
          title={senderName || t('Unknown user')}
        >
          <div
            className={clsx('w-10 rounded-full text-white')}
            style={{ backgroundColor: avatarImageSrc ? undefined : displayColor }}
          >
            {avatarImageSrc && <img src={avatarImageSrc} />}
            {!avatarImageSrc && (
              <span className="text-xs w-full h-full flex items-center justify-center">
                {senderName ? senderName[0].toLocaleUpperCase() : '?'}
              </span>
            )}
          </div>
        </div>

        {/* Bubble */}
        <div
          className={clsx(
            'flex flex-col',
            'py-1 px-2',
            'chat-bubble text-black !rounded-btn',
            hideAvatar && 'before:content-none !rounded-btn',
            !fromAuthenticatedUser && 'bg-white',
            fromAuthenticatedUser && 'bg-[#d9fdd3]',
            !fromAuthenticatedUser && !hideAvatar && '!rounded-es-none',
            fromAuthenticatedUser && !hideAvatar && '!rounded-ee-none',
            'cursor-pointer',
            'rounded-xl'
          )}
          onContextMenu={event => props.onContextMenu?.(event)}
        >
          <WhatsAppTitle name={senderName} displayPhoneNumber={displayPhoneNumber} displayColor={displayColor} />
          <WhatsAppSubtitle
            forwarded={(message.data as any)?.whatsapp?.context?.forwarded}
            frequentlyForwarded={(message.data as any)?.whatsapp?.context?.frequentlyForwarded}
          />
          {/* TODO: Add context component when implements context info */}
          <div className="inline-flex gap-2">
            <WhatsAppContent
              className="grow pb-1 break-words overflow-hidden"
              content={message.content}
              contentType={message.contentType}
              contentData={message.contentData}
              files={message.files}
              fromAuthenticatedUser={fromAuthenticatedUser}
            />
            <WhatsAppFooter
              sendDate={message.sendDate}
              messageStatus={message.status}
              hideStatusIcon={!fromAuthenticatedUser}
            />
          </div>
          <ChatSourcesBubbleContent message={message} />
        </div>
      </div>

      {/* Error */}
      {message.status === 'failed' && (
        <div className="flex items-center justify-center">
          <span className="tooltip" data-tip={errorMessage}>
            <InfoIcon className="text-error" size={24} />
          </span>
        </div>
      )}
    </div>
  )
}

function getDisplayColor (value: string) {
  const hash = getHashOfString(value)
  const h = normalizeHash(hash, 0, 360)
  const s = normalizeHash(hash, 50, 75)
  const l = normalizeHash(hash, 25, 60)

  return `hsl(${h}, ${s}%, ${l}%)`
}
